// React Router v7 compatibility layer for legacy history usage
// This provides a shim for components still using the old history.push() API

// Re-export React Router hooks so components importing from '@/history' work
export { useNavigate, useLocation } from 'react-router';

type NavigateOptions = { replace?: boolean; state?: any };
type NavigateFn = (to: string, options?: NavigateOptions) => void;

let navigateFunction: NavigateFn | null = null;

export function setNavigateFunction(fn: NavigateFn): void {
  navigateFunction = fn;
  console.log('[history] Navigate function initialized');
}

export interface History {
  push: (path: string, state?: any) => void;
  replace: (path: string, state?: any) => void;
  goBack: () => void;
  goForward: () => void;
  go: (n: number) => void;
  location: Location | null;
}

// Create the history object with all methods
const history: History = {
  push(path: string, state?: any): void {
    console.log('[history] push called with path:', path);
    if (navigateFunction) {
      navigateFunction(path, { state });
    } else {
      console.error('[history] Navigate function not set. Navigation to', path, 'cannot proceed.');
      // Fallback to window.location for critical navigation
      if (typeof window !== 'undefined') {
        window.location.href = path;
      }
    }
  },
  replace(path: string, state?: any): void {
    console.log('[history] replace called with path:', path);
    if (navigateFunction) {
      navigateFunction(path, { replace: true, state });
    } else {
      console.error('[history] Navigate function not set. Navigation to', path, 'cannot proceed.');
      if (typeof window !== 'undefined') {
        window.location.replace(path);
      }
    }
  },
  goBack(): void {
    console.log('[history] goBack called');
    if (typeof window !== 'undefined') {
      window.history.back();
    }
  },
  goForward(): void {
    console.log('[history] goForward called');
    if (typeof window !== 'undefined') {
      window.history.forward();
    }
  },
  go(n: number): void {
    console.log('[history] go called with:', n);
    if (typeof window !== 'undefined') {
      window.history.go(n);
    }
  },
  // Add location property for compatibility
  get location(): Location | null {
    if (typeof window !== 'undefined') {
      return window.location;
    }
    return null;
  }
};

// Log when module loads
if (typeof console !== 'undefined') {
  console.log('[history] Module loaded');
}

// Named export
export { history };

// Default export
export default history;
